import { useEffect, useRef, useState } from 'react';

/**
 * Two-timer skeleton-control hook (FB / LinkedIn / YouTube pattern).
 *
 * Naive `loading ? <Skeleton/> : <Content/>` produces two visual glitches:
 *   1. **Fast-load blink** — API resolves in 80ms; skeleton mounts and
 *      unmounts before the eye registers it. Worse than no skeleton.
 *   2. **Premature unmount** — skeleton appears at 200ms, data arrives
 *      20ms later → skeleton flashes for 20ms then vanishes.
 *
 * Fix: gate the visible flag on two timers.
 *   - `showAfter` (default 200ms) — only flip true if loading lasts longer.
 *     Cached / fast responses skip the skeleton entirely.
 *   - `minVisible` (default 400ms) — once true, hold for at least this long
 *     even after the underlying `loading` flag flips back to false.
 *
 * Returns the deferred boolean. Drop-in for `loading` in three-state guards.
 */
interface DeferredLoadingOpts {
  showAfter?: number;
  minVisible?: number;
}

export function useDeferredLoading(
  loading: boolean,
  { showAfter = 200, minVisible = 400 }: DeferredLoadingOpts = {},
): boolean {
  const [show, setShow] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      // Loading just started. Schedule skeleton to appear after showAfter ms.
      // If loading completes before then, the cleanup cancels the timer and
      // skeleton never renders.
      const t = setTimeout(() => {
        setShow(true);
        shownAtRef.current = Date.now();
      }, showAfter);
      return () => clearTimeout(t);
    }

    // Loading just ended.
    if (shownAtRef.current === null) {
      // Skeleton was never shown — nothing to do.
      setShow(false);
      return;
    }

    // Skeleton was shown. Hold it for at least `minVisible` total ms.
    const elapsed = Date.now() - shownAtRef.current;
    const remaining = Math.max(0, minVisible - elapsed);
    const t = setTimeout(() => {
      setShow(false);
      shownAtRef.current = null;
    }, remaining);
    return () => clearTimeout(t);
  }, [loading, showAfter, minVisible]);

  return show;
}
